import { create } from 'zustand';
import type { Project } from '@/storage/ProjectRepository';
import type { AppDefinition } from '@/types/app';
import { buildWebAppRecord, markUnfinished, newWebAppId } from '@/webapp/webAppRecord';
import { displayAddress } from '@/webapp/siteAddress';
import type { AppPlan } from '@/webapp/plan/planTypes';
import type { StyleBrief } from '@/llm/planTypes';
// Pure: the rule for when a stored key is still worth sending, shared with the phone.
import { fingerprintOf, intentChoices, resumableIntent, sameIntent } from '@/store/buildIntent';
import { chatStore, projectStore, type NewProject } from '@/storage/projectStorePort';
import { ApiError, type ApiErrorKind } from '../api/client';
import { authConfigured, signedInUserId } from '../api/auth';
import { BUILD_TIMEOUT_MS, buildApp, heldPreviewUrl, isBuildInProgress, type BuiltApp } from '../api/build';
import type { OpenedLink } from '../api/emailLink';
import { adoptAnonymousId } from './anonymous';
import { publishToSaddle } from '../api/hosting';
import { forgetUnhostedApp, keepUnhostedApp, registeredAppIdOf } from './unhostedApp';
import { schemasForPublish } from './publishSchemas';
import { markPublished } from './livePage';
import { uploadTextAsset } from '../api/textAsset';
import { uploadAllMedia } from '../api/media';
// The PURE splitter and slug rule — the same two the SERVER runs. Reaching for them through
// the shared textAsset module would drag Cognito, and React Native, into the browser.
import { previewParts, textAssetName } from '@/webapp/documentSplit';
// Pure: the seeder's reader of an app's S.list starting rows, for the photos a gallery asks for.
import { startingRowPhotoNames } from '@/webapp/seedOwnerContent';
import { useDraft, type Draft } from './draft';
import {
  clearIntent,
  isOwnAttempt,
  markOwnAttempt,
  patchIntent,
  readIntent,
  writeIntent,
  type WebBuildIntent,
} from './buildIntent';
import { trackPixel } from '../analytics/metaPixel';
import { funnelStep } from '../analytics/funnel';

/**
 * A build, owned by the browser.
 *
 * The shared buildRunStore polls a queued job, keeps the screen awake, and branches across
 * three runtimes — and it reaches the React Native engine to do it, which is the only
 * reason this client ever needed a `registerAll` stub. The web needs none of that: the
 * server's /build is synchronous and returns the finished document, and the browser only
 * ever builds web apps.
 *
 * It lives outside the screen on purpose, so navigating away does not cancel a build.
 *
 * And it writes down what was asked for BEFORE asking, because leaving the page does: a phone
 * browser discards a backgrounded tab, a reload clears memory, and the server builds — and
 * charges — regardless. So a build reserves its project row first (marked unfinished, holding
 * the idea, the name, the plan and the look) and persists its intent with the idempotency key
 * (state/buildIntent.ts). Whatever happens to the page, the row is there to open and its Try
 * again sends the same key, which the server answers with the finished build, free. This is
 * the phone's rule (8c0e40c, 5516d29), which only the phone had.
 */
export type BuildStatus = 'idle' | 'running' | 'error';

export interface BuildRun {
  status: BuildStatus;
  /** What is happening, for the overlay. */
  phase: string;
  error: string | null;
  /** True when the failure is one retrying cannot fix. */
  terminal: boolean;
  /**
   * True when the failure was a refusal for CREDITS. Retrying meets the same refusal every
   * time, and "Try again" was the overlay's only way forward; this is what lets it offer the
   * way that works — buying credits — instead.
   */
  outOfCredits: boolean;
  /**
   * The client's kind for the failure, when it was the server's answer. The overlay offers Sign in
   * for 'unauthorized' (F15, 2026-09-30): that failure is terminal, and Close alone left somebody
   * whose session had ended with no way back to the page they were on.
   */
  errorKind: ApiErrorKind | null;
  /** The project row this build fills: reserved before it starts, null when none could be. */
  projectId: string | null;
  /**
   * The overlay is put away to a pill ("Keep browsing", F15). Here, not in the overlay, because it
   * decides what a build that lands does: put away, it must not take the person anywhere.
   */
  minimised: boolean;
  /**
   * A build that landed while put away, waiting to be opened. Navigating on its own took the
   * person out of wherever they had browsed to — another project's chat, with an unsent message
   * and picked files that live only in that screen (review of F15, 2026-09-30).
   */
  landed: { projectId: string; name: string } | null;
  /**
   * A build finished before the card (2026-10-04): the app waits on the server until the trial pays
   * for it, and the plan screen opens the offer saying so. Nothing is saved or published yet; the
   * build's key is kept, so the build after checkout collects this same app rather than a new one.
   */
  heldApp: { name: string; previewUrl?: string } | null;
  /**
   * Running behind the trial offer (2026-10-05): Build opened the offer at once and the build goes
   * on under it, its progress shown on the panel (Paywall.tsx) — so the overlay stays out of the
   * way. Off again when the build ends, fails, or the offer is closed (then it is the pill).
   */
  behindOffer: boolean;
  minimise: () => void;
  expand: () => void;
  /** Go to the build that landed, and stop offering it. */
  openLanded: () => void;
  /** Stop offering it: the person is already there. */
  forgetLanded: () => void;
  /**
   * Build the draft. `from` is Try again's: the attempt that failed, built as it was asked for
   * rather than as the draft reads now.
   */
  /**
   * `prebuilt`: an app already built for this person (a personal code's, api/billing.ts
   * PreparedApp) — finished exactly as a build /build answered would be, without asking /build.
   */
  start: (ownerId: string, from?: BuildInput, opts?: { behindOffer?: boolean; prebuilt?: BuiltApp }) => Promise<void>;
  /**
   * Ask again for the build this browser has on record, under its key, after a sign-in or a reload
   * (the reveal screen, 2026-10-08): a held build comes back with its preview, a running one is
   * waited for. Puts its choices back in the draft too. False when there is none for this account.
   */
  collect: (ownerId: string) => Promise<boolean>;
  retry: () => void;
  dismiss: () => void;
  /** Close the overlay and go where credits are bought — the way past an out-of-credits refusal. */
  getCredits: () => void;
  /** Say, once, that a build this browser started never came back — the page closed mid-build. */
  recover: (ownerId: string) => void;
  /** The unfinished row's Try again: its saved choices go back into the draft, and it is built. */
  resumeProject: (project: Project, ownerId: string) => Promise<void>;
}

/** Where to go when a build finishes. Injected so the store owns no routing. */
type Navigate = (path: string, opts?: { replace?: boolean }) => void;
let navigate: Navigate | null = null;
export function setBuildNavigate(fn: Navigate | null): void {
  navigate = fn;
}

let lastOwnerId: string | null = null;

/**
 * The interrupted build `recover()` just reported, which the overlay's Try again promises to
 * pick up. Held so that promise is kept even when the draft has meanwhile filled with
 * something else — a landing page's `?idea=` arrives on the same load.
 */
let reported: WebBuildIntent | null = null;

/**
 * The build in flight, or the one that just failed, exactly as it was asked for — idea, name,
 * plan, look, files and reserved row. What Try again builds (review of F15, 2026-09-30): once the
 * overlay could be put away, the draft could become another idea before this one failed.
 */
let attempt: BuildInput | null = null;

/*
 * The key for the build being attempted lives in the persisted intent, and only there.
 *
 * It was a module variable. That survived `retry()` — which re-runs the whole build, so a key
 * minted per attempt makes the server see two intents and charge for both — and died with the
 * page, so a build the server finished while the tab was discarded or reloaded could never be
 * asked for again: the next attempt minted a new key and paid a second time.
 *
 * One lifetime now, the intent's. `start()` writes it; a failure, a dismissal and a reload all
 * KEEP it (marked `reported`); only a build that lands clears it. What stops a different idea
 * inheriting the old key and row is `sameIntent`, checked when the next build starts — the
 * same rule, and the same code, as the phone.
 */

/** `crypto.randomUUID` is not available everywhere this code runs; these two always are. */
const newRequestKey = (): string =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;

/** What a reserved row says until its build lands — which is also what it says if the page dies mid-build. */
const NOT_BUILT = 'This app has not been built yet. Your choices are saved — Try again builds it from them.';

/**
 * A connection lost while the model works. An iPhone that switches apps suspends the tab and the
 * fetch fails as "Check your connection" — while the paid build runs on. Try again sends the same
 * key, which waits for that build rather than paying for another; the sentence has to say so.
 */
const PROBABLY_RUNNING =
  'Your build is probably still running on our side — keep this page open and press Try again. It asks for this same build, so you are not charged twice.';

/** The server's "wait, not pay", said so a person waits instead of paying. */
const STILL_RUNNING =
  'That build is still running. Give it a moment rather than starting again — you have not been charged twice.';

/** Said when the account that started a build is no longer the one signed in. */
const ACCOUNT_CHANGED =
  'You signed out, or signed in as another account, while this was building, so it was stopped. '
  + 'Sign back in to the account that started it and press Try again on its row in My Apps. If it finished on our side meanwhile, it comes back without a second charge.';

/**
 * Stop unless the account that started this build is still the one signed in.
 *
 * `request()` reads the token for every call, so a build outlives the session it began in: the
 * POST /build goes out as account A, and a publish after a sign-out and a sign-in as B goes out as
 * B — A's app and files published into B's account, and the row saved under A (review of the
 * Account tab, 2026-09-30). Asked of the SAME store the token comes from, not the React session,
 * which another tab's sign-in never reaches. A read that fails is not a yes.
 *
 * With no pool configured nobody can be signed in, so nobody can have become someone else — and
 * no build gets this far, because /build refuses a request without a token.
 */
async function assertStillSignedInAs(ownerId: string): Promise<void> {
  if (!authConfigured()) return;
  const now = await signedInUserId().catch(() => null);
  if (now !== ownerId) throw new ApiError('unauthorized', ACCOUNT_CHANGED);
}

/**
 * The project row for this intent, before anything that can fail.
 *
 * `create` used to run only after the build and the publish returned, so a build that died —
 * or a page that did — left nothing: the idea, the features, the layout and the look lived in
 * the draft, which is memory. Write it down before doing the thing that can fail.
 *
 * A retry of the same intent reuses its row; only a new intent reserves one, or every attempt
 * would add another. A row deleted meanwhile is not written to. Reserving is a convenience,
 * not the build: if storage refuses, the build still runs and only the resume is lost.
 */
async function reserve(
  existing: string | null,
  input: { appName: string; goal: string; vibe: string | null; plan: AppPlan | null },
  ownerId: string,
  requestKey: string,
): Promise<string | null> {
  try {
    if (existing && (await projectStore().getById(existing, ownerId))) return existing;
    const row = await projectStore().create(
      {
        name: input.appName,
        description: input.goal,
        definition: markUnfinished(
          buildWebAppRecord({
            // A PLACEHOLDER, so the record is well-formed. The server mints the real id — the
            // page's storage namespace — and the finished definition replaces this one whole.
            id: newWebAppId(input.appName),
            appName: input.appName,
            goal: input.goal,
            source: '',
            vibe: input.vibe,
            plan: input.plan,
          }),
          NOT_BUILT,
        ),
      },
      ownerId,
    );
    // Written down at once, so the next attempt — after a failure or a reload — fills THIS row.
    patchIntent(requestKey, { reservedId: row.id });
    return row.id;
  } catch {
    return null;
  }
}

/** The finished app, into its reserved row — or a new one when there is no row to fill. */
async function save(reservedId: string | null, fields: NewProject, ownerId: string): Promise<{ id: string }> {
  if (reservedId && (await projectStore().getById(reservedId, ownerId))) {
    // The whole definition is replaced, so the unfinished mark goes with the placeholder.
    await projectStore().update(reservedId, fields, ownerId);
    return { id: reservedId };
  }
  return projectStore().create(fields, ownerId);
}

/**
 * Say on the row itself why it is unfinished.
 *
 * The overlay's message belongs to this page and dies with it; the person may come back to the
 * row tomorrow, and "not built yet" tells them nothing they cannot see. Best-effort: a note
 * that fails to write must not become a second failure.
 */
async function noteUnfinished(reservedId: string, ownerId: string, reason: string): Promise<void> {
  try {
    const row = await projectStore().getById(reservedId, ownerId);
    if (!row) return;
    await projectStore().update(reservedId, { definition: markUnfinished(row.definition, reason) }, ownerId);
  } catch {
    /* the overlay already said it */
  }
}

/**
 * The catalog app this intent's page is ALREADY published as — read when the build returns,
 * not only when it started.
 *
 * Another tab can be building the same intent (same idea, same key): the second one's /build
 * waits out the first's 409 and is then handed the same finished app. Captured only at the
 * start, the id was not yet known to the second, which published without it and registered a
 * second catalog app at `<slug>-2`, left the first page public with nothing pointing at it,
 * and counted a second AppPublished. The first writes the id to the intent the moment it
 * publishes and into the reserved row when it saves — then clears the intent — so both are
 * asked.
 */
async function publishedAs(requestKey: string, reservedId: string | null, ownerId: string): Promise<string | undefined> {
  const intent = readIntent();
  if (intent?.requestKey === requestKey && intent.hostedAppId) return intent.hostedAppId;
  if (!reservedId) return undefined;
  try {
    const row = await projectStore().getById(reservedId, ownerId);
    return (row?.definition as AppDefinition | undefined)?.hostedAppId || undefined;
  } catch {
    return undefined;
  }
}

/**
 * Whether a build this browser sent may still be running, in some tab that is waiting for it.
 *
 * `recover()` runs on every signed-in page load, and an unreported intent looks the same
 * whether its page died or another tab is still building it. Reporting the second as "the page
 * closed" was worse than wrong: its Try again joined the live build and published it again.
 * So a build is only called dead when THIS tab sent it (this page is new, so nothing is
 * listening), or when it is older than any build can run. A young one from another tab is left
 * unreported, so a later load still says so if that tab really did close.
 */
function mayStillBeRunning(intent: WebBuildIntent): boolean {
  if (isOwnAttempt(intent.attemptId)) return false;
  // Records from before `attemptAt` existed: their first attempt began when the key did.
  const sent = Date.parse(intent.attemptAt ?? intent.startedAt ?? '');
  // An unknown age is an old client's record, and any build it described has long ended.
  return Number.isFinite(sent) && Date.now() - sent < BUILD_TIMEOUT_MS;
}

/**
 * What one build is made from: the draft's choices and files, and the row it fills when it has one.
 *
 * A snapshot, taken when Build is pressed (`fromDraft`), so Try again and the overlay never read a
 * draft that became another idea while this one ran. It carries the files too — the photos with
 * their descriptions and the page documents go to /build from HERE, and they are part of the key's
 * fingerprint. The "Anything else" words need no field: Build puts them on the plan before it
 * starts (Plan.tsx, `withAnythingElse`).
 */
export type BuildInput = Pick<
  Draft,
  'description' | 'appName' | 'plan' | 'vibe' | 'styleBrief' | 'kind' | 'attachedDoc' | 'attachedDocs' | 'attachedMedia'
> & { reservedId?: string | null };

/** The draft as Build reads it, at the moment it is pressed. */
function fromDraft(): BuildInput {
  const d = useDraft.getState();
  return {
    description: d.description,
    appName: d.appName,
    plan: d.plan,
    vibe: d.vibe,
    styleBrief: d.styleBrief,
    kind: d.kind,
    attachedDoc: d.attachedDoc,
    attachedDocs: d.attachedDocs ?? [],
    attachedMedia: d.attachedMedia,
  };
}

/**
 * A stored intent's choices, as a build reads them. Its files did not survive the page — which is
 * why `missingFiles` is asked of it before it is built: an intent that had any sends the person
 * back to choose them rather than building without them (F4).
 */
function inputFromIntent(intent: WebBuildIntent): BuildInput {
  return {
    description: intent.goal,
    appName: intent.appName,
    plan: intent.plan ?? null,
    vibe: intent.vibe ?? null,
    styleBrief: intent.styleBrief ?? null,
    kind: intent.kind ?? 'personal',
    attachedDoc: null,
    attachedDocs: [],
    attachedMedia: [],
    reservedId: intent.reservedId ?? null,
  };
}

/** What a build is given to work from: its choices and its files — a BuildInput, or the draft itself. */
type BuildChoices = Pick<Draft, 'plan' | 'styleBrief' | 'vibe' | 'attachedDoc' | 'attachedDocs' | 'attachedMedia'>;
/** Only the files. */
type BuildFiles = Pick<Draft, 'attachedDoc' | 'attachedDocs' | 'attachedMedia'>;

/** The names a build's attachments go by: what the intent keeps, and what a resume checks. */
type IntentAttachments = NonNullable<WebBuildIntent['attachments']>;

function attachmentsOf(input: BuildFiles): IntentAttachments | null {
  const media = input.attachedMedia.map((m) => m.name);
  const documents = (input.attachedDocs ?? []).map((d) => d.name);
  const text = input.attachedDoc?.title;
  if (!media.length && !documents.length && !text) return null;
  return { media, documents, ...(text ? { text } : {}) };
}

function namesOf(a: IntentAttachments): string[] {
  return [...a.media, ...a.documents, ...(a.text ? [a.text] : [])];
}

/**
 * The fingerprint the key belongs to: the shared choices, and what was attached (F4).
 *
 * The server replays whatever it stored under a key, so the same idea with different photos,
 * sent under the old key, came back as the old app naming photos nobody uploaded. Attachments
 * are part of the build now — and with none, the fingerprint is exactly the shared one, which
 * the phone computes too.
 *
 * A photo counts by NAME, not by its description. Re-choosing the same files after a reload must
 * find the build that was paid for, and a description is re-drafted by the AI on every pick —
 * counting it would make every recovery a second charge. Documents count by their text, which a
 * re-chosen file reproduces exactly.
 */
function choicesOf(input: BuildChoices): string {
  const shared = intentChoices({ plan: input.plan ?? null, styleBrief: input.styleBrief ?? null, vibe: input.vibe ?? null });
  if (!attachmentsOf(input)) return shared;
  const doc = input.attachedDoc;
  return fingerprintOf({
    choices: shared,
    media: input.attachedMedia.map((m) => m.name),
    documents: (input.attachedDocs ?? []).map((d) => ({ name: d.name, text: fingerprintOf(d.text) })),
    text: doc ? { title: doc.title, attribution: doc.attribution, headingPattern: doc.headingPattern, text: fingerprintOf(doc.text) } : null,
  });
}

/**
 * What an intent was built with that `have` no longer holds — or null when nothing is missing.
 * `have` is what the next build would be given: the draft, or a BuildInput (one made from an
 * intent never holds files, so every file that intent had is missing).
 *
 * An empty list is a record from before the names were kept: something was attached, and which
 * is not known. A draft that still holds its files (a failure without a reload) misses nothing.
 */
function missingFiles(intent: WebBuildIntent | null | undefined, have: BuildFiles): string[] | null {
  if (!intent?.hadAttachment) return null;
  const want = intent.attachments;
  if (!want) return attachmentsOf(have) ? null : [];
  const media = new Set(have.attachedMedia.map((m) => m.name));
  const docs = new Set((have.attachedDocs ?? []).map((d) => d.name));
  const missing = [
    ...want.media.filter((n) => !media.has(n)),
    ...want.documents.filter((n) => !docs.has(n)),
    ...(want.text && !have.attachedDoc ? [want.text] : []),
  ];
  return missing.length ? missing : null;
}

/** "a", "a and b", "a, b and c". */
function listed(names: string[]): string {
  return names.length < 2 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/**
 * The photo names a page asks for: each literal `S.media('…')`, and each one its S.list starting
 * rows hand to S.media — a gallery's `S.media(p.imageUrl)` over rows seeded as 'porch.jpg'.
 */
function mediaNamesIn(source: string): string[] {
  const names = new Set<string>();
  for (const m of source.matchAll(/S\.media\(\s*(['"`])([^'"`]+?)\1\s*\)/g)) names.add(m[2]);
  for (const n of startingRowPhotoNames(source)) names.add(n);
  return [...names];
}

/** Put a stored intent's choices back where the build reads them. */
/**
 * The "email me my app link" email, opened here — most likely another browser than the one the
 * build began in (api/emailLink.ts). This browser takes on that one's id and the build's intent,
 * so signing up here collects the same build under the same key, as it would have there.
 */
export function takeUpEmailLink(opened: OpenedLink): void {
  adoptAnonymousId(opened.anonId);
  const intent: WebBuildIntent = {
    ownerId: '',
    goal: opened.goal,
    appName: opened.appName,
    plan: opened.plan ?? null,
    signedIn: false,
    hadAttachment: false,
    requestKey: opened.requestKey,
    startedAt: opened.createdAt || new Date().toISOString(),
    reported: true,
    held: true,
  };
  writeIntent(intent);
  restoreDraft(intent);
  if (opened.state === 'done') useBuildRun.setState({ heldApp: { name: opened.appName } });
}

function restoreDraft(intent: {
  goal: string;
  appName: string;
  plan?: AppPlan | null;
  vibe?: string | null;
  styleBrief?: StyleBrief | null;
  kind?: 'personal' | 'sharable';
}): void {
  useDraft.setState({
    description: intent.goal,
    appName: intent.appName,
    plan: intent.plan ?? null,
    vibe: intent.vibe ?? null,
    styleBrief: intent.styleBrief ?? null,
    kind: intent.kind ?? 'personal',
  });
}

/** What recover() says about files a closed page took with it — by name, when the record has them. */
function filesLeftWith(intent: WebBuildIntent): string {
  const names = intent.attachments ? namesOf(intent.attachments) : [];
  const them = names.length === 1 ? 'it' : 'them';
  const what = names.length ? `the files you attached (${listed(names)})` : 'the file you attached';
  return `\n\nYour idea is saved, but ${what} left with the page. Try again takes you back to choose ${them} again, then Build picks it up — if it finished on our side meanwhile, the same files bring it back without a second charge.`;
}

/**
 * Close the overlay and open the wizard where the files are chosen, with the choices restored.
 * The intent stays as it is — reported, with its key and row — so Build from there, with the same
 * files, is the same build.
 */
function chooseFilesAgain(): void {
  reported = null;
  useDraft.getState().setPlanStep('content');
  useBuildRun.setState({ status: 'idle', phase: 'queued', error: null, terminal: false, outOfCredits: false });
  navigate?.('/new/plan');
}

export const useBuildRun = create<BuildRun>((set, get) => ({
  status: 'idle',
  phase: 'queued',
  error: null,
  terminal: false,
  outOfCredits: false,
  errorKind: null,
  projectId: null,
  minimised: false,
  landed: null,
  heldApp: null,
  behindOffer: false,

  start: async (ownerId: string, from?: BuildInput, opts?: { behindOffer?: boolean; prebuilt?: BuiltApp }) => {
    /*
     * One build at a time — said, not swallowed. With the overlay put away, Build on another idea
     * is reachable while this one runs, and a press that did nothing looked broken. The running
     * build comes back into view instead, which is the reason.
     */
    if (get().status === 'running') {
      set({ minimised: false });
      return;
    }
    lastOwnerId = ownerId;
    reported = null;
    attempt = null;

    const draft = from ?? fromDraft();
    const goal = draft.description.trim();
    if (!goal) {
      set({ status: 'error', error: 'Describe the app you want first.', terminal: true, outOfCredits: false, errorKind: null });
      return;
    }
    const appName = draft.appName || draft.plan?.appName || 'My app';
    // What Try again builds: THIS, whatever the draft becomes while it runs (see `attempt`).
    attempt = { ...draft };

    /*
     * Continue the intent on record before starting a new one — whether this is `retry()`, a
     * Build pressed again on the same choices, or a row's Try again after a reload. A build the
     * server finished while this page never heard back is recoverable only under the same key.
     * A record for a DIFFERENT idea, or another account, is left alone: neither its key nor its
     * row is handed to this build.
     */
    const stored = readIntent();
    /*
     * A build begun signed out (ownerId '') is the build of whoever signs in next in this browser —
     * its key is what lets the server hand the held app to that account (2026-10-08).
     */
    const same = stored && (stored.ownerId === ownerId || stored.ownerId === '') && sameIntent(stored, { appName, goal }) ? stored : null;
    /*
     * The same BUILD, too, before its key is reused — the phone's rule. The server replays what it
     * stored under a key without comparing what it is sent, so the same name and sentence with new
     * features, a new layout or a new look, sent under the old key, came back as the OLD app. The
     * row and the page stay this app's either way (`same`); the key belongs to one set of choices.
     */
    const choices = choicesOf(draft);
    const attachments = attachmentsOf(draft);
    const resumable = resumableIntent(same, { appName, goal, choices });
    const requestKey = resumable?.requestKey ?? newRequestKey();
    const attemptId = newRequestKey();
    markOwnAttempt(attemptId);
    writeIntent({
      ownerId,
      appName,
      goal,
      signedIn: true,
      hadAttachment: !!attachments,
      ...(attachments ? { attachments } : {}),
      requestKey,
      // The KEY's age, which is what the server's replay window measures.
      startedAt: resumable?.startedAt ?? new Date().toISOString(),
      // A key the server holds an app under keeps that app's longer window.
      ...(resumable?.held ? { held: true } : {}),
      // THIS attempt's, which is what says whether it may still be running (recover()).
      attemptAt: new Date().toISOString(),
      attemptId,
      // The row and the published page survive a key that aged out: same app either way.
      ...(same?.reservedId ? { reservedId: same.reservedId } : {}),
      ...(same?.hostedAppId ? { hostedAppId: same.hostedAppId } : {}),
      ...(same?.registeredAppId ? { registeredAppId: same.registeredAppId } : {}),
      plan: draft.plan ?? null,
      styleBrief: draft.styleBrief ?? null,
      vibe: draft.vibe ?? null,
      kind: draft.kind,
      choices,
      // In flight again, so a page that dies from here on is news for the next load.
    });
    const hostedBefore = same?.hostedAppId;

    set({ status: 'running', phase: 'generating', error: null, terminal: false, outOfCredits: false, errorKind: null, projectId: same?.reservedId ?? null, minimised: false, landed: null, heldApp: null, behindOffer: opts?.behindOffer === true });
    // Signed out there is nowhere to keep a project; the account that claims the build gets one.
    const reservedId = !ownerId ? null : await reserve(
      same?.reservedId ?? draft.reservedId ?? null,
      { appName, goal, vibe: draft.vibe, plan: draft.plan ?? null },
      ownerId,
      requestKey,
    );
    set({ projectId: reservedId });
    if (attempt) attempt.reservedId = reservedId;
    /** The row holds the finished app: from here a failure must not mark it unfinished. */
    let saved = false;

    try {
      /*
       * The manifest of an attached document — what it is called and what its parts are —
       * computed with the server's OWN splitter, so the ids the generated app addresses with
       * `S.asset(name, id)` are the ids that will exist. A mismatch here is a 404 on a
       * published page.
       *
       * The TEXT is deliberately absent. It goes up after the app exists, because there has
       * to be an app id to hang it on.
       */
      const doc = draft.attachedDoc;
      const textAsset = doc
        ? {
          name: textAssetName(doc.title),
          title: doc.title,
          attribution: doc.attribution,
          parts: previewParts(doc.text, doc.headingPattern || undefined).map((part) => ({
            id: part.id,
            label: part.label,
          })),
        }
        : null;

      // Already built for them (a personal code's app): finished from here exactly as /build's would be.
      const built = opts?.prebuilt ?? await buildApp({
        goal,
        appName,
        requestKey,
        vibe: draft.vibe ?? null,
        plan: draft.plan ?? null,
        textAsset,
        /*
         * Names, not URLs — nothing is uploaded yet and will not be until the app exists — each
         * with the description under its thumbnail (contract C2), so the hero and the alt text
         * follow what is in the photo rather than what its file is called.
         */
        media: draft.attachedMedia.map((m) => (m.description !== undefined ? { name: m.name, description: m.description } : { name: m.name })),
        // The short texts to put ON the page — a menu, a schedule — which the model must read.
        documents: draft.attachedDocs ?? [],
        style: draft.styleBrief ?? null,
      });

      // Built before the card: nothing to save or publish until the trial pays for it.
      if (built.held) {
        // Kept, with its key, for the build after checkout; told, so recover() does not call it lost.
        patchIntent(requestKey, { reported: true, held: true });
        set({
          status: 'idle', phase: 'queued', error: null, minimised: false, behindOffer: false,
          heldApp: { name: built.appName || appName, ...(built.previewToken ? { previewUrl: heldPreviewUrl(built.previewToken) } : {}) },
        });
        return;
      }
      // The app built before the card, handed over now that the trial has paid for it.
      if (built.released) funnelStep('site_revealed');

      /*
       * Publishing is a SEPARATE step and its failure must not cost the build. The
       * generated source is the expensive part and it is already in hand; throwing it away
       * over a transient network error would lose real work. The project is created either
       * way and the summary says plainly whether there is a link yet.
       */
      // The build took minutes, and the person may have signed out, or in as somebody else, since.
      await assertStillSignedInAs(ownerId);
      set({ phase: 'publishing' });
      // Asked again now: another tab may have published this same intent while we waited.
      const hosted = (await publishedAs(requestKey, reservedId, ownerId)) ?? hostedBefore;
      let definition = buildWebAppRecord({
        // The SAME id the document was assembled under: it is the storage namespace, so a
        // different one would strand the data of anyone already using the app.
        id: built.appId,
        appName: built.appName || appName,
        goal,
        source: built.appCode,
        vibe: draft.vibe,
        plan: draft.plan ?? null,
      });
      /*
       * An earlier attempt of this intent already put a page up — the publish worked and
       * something after it did not. Publishing again must REPLACE that page, not register a
       * second listing at `<slug>-2` and leave the first public with nothing pointing at it.
       */
      if (hosted) definition = { ...definition, hostedAppId: hosted };

      let siteUrl: string | undefined;
      /** The server said the page is being served — only then does the message say it is live. */
      let servedAt: string | undefined;
      let publishError: string | null = null;
      /** A published app that is missing something it was built to use. Said, not swallowed. */
      let attachmentProblem: string | null = null;
      /** The photos that reached storage, which went up with the page and go on the record. */
      let uploadedMedia: { id: string; url: string }[] = [];
      /** The ones that did not, named in the summary. */
      let failedMedia: string[] = [];
      /** A catalogue id registered for this app with no page up under it yet (F21). */
      let registeredAppId: string | undefined = readIntent()?.requestKey === requestKey
        ? readIntent()?.registeredAppId
        : undefined;
      try {
        const published = await publishToSaddle({
          definition,
          name: definition.name,
          description: goal,
          html: built.html,
          /*
           * The collections this app declares, WITH what each field means.
           *
           * This client sent nothing, so the server fell back to reading the document
           * (`schemasFromDocument`) — which works, and can only ever say `type: 'string'`,
           * because source code shows a field being written and not what it is. The plan
           * knows `strokes` is a number; publish is the one moment the two are in the same
           * place, and sending it is the difference between data somebody can later ask a
           * question about and a pile of text.
           *
           * The phone already did this. Sending it here is what stops the two clients
           * publishing differently-described versions of the same app — and the SAME
           * function runs on every later publish from the project (PublishPanel), which
           * sent nothing and so rewrote these rows without the plan's rules.
           */
          schemas: schemasForPublish(built.appCode, draft.plan),
          // An earlier attempt's row with no page yet: published under it, not beside it (F21).
          ...(registeredAppId ? { registeredAppId } : {}),
          /*
           * The photos go up once the app has its catalogue id and BEFORE its page does, and
           * their addresses go up WITH the page — the row's list is what the page reads
           * `S.media` from. They used to be uploaded after the publish and kept on this
           * browser's record only, so a new app's photos were stored and never shown.
           *
           * What actually REACHED storage, not what was offered: the app was written against
           * these names, and a file that failed is simply absent — `S.media` answers null for
           * it rather than yielding a dead address. Asked again under a new id if the one this
           * intent pointed at is gone, so only the last attempt's answer counts.
           */
          ...(draft.attachedMedia.length
            ? {
              attach: async (catalogueId: string) => {
                await assertStillSignedInAs(ownerId);
                ({ uploaded: uploadedMedia, failed: failedMedia } = await uploadAllMedia(catalogueId, draft.attachedMedia));
                return uploadedMedia;
              },
            }
            : {}),
        });
        // The page is up under a catalogue id the record keeps: nothing unhosted is left over.
        registeredAppId = undefined;
        siteUrl = published.siteUrl;
        // Absent reads as live, as everywhere a publish is answered: only an explicit false is off.
        if (published.webPublished !== false) servedAt = published.siteUrl;
        // The catalogue id, kept on the project: every later publish, submission list and
        // member screen resolves through it, and minting a second one would move the
        // app's address.
        /*
         * Marked as carrying this source, too. Without it every freshly built app would read
         * as "may be behind" and offer "Publish changes" for a page that has exactly this app.
         */
        definition = markPublished(
          { ...definition, webUrl: published.siteUrl, hostedAppId: published.appId },
          built.appCode,
        );
        // Written down the moment it is live, so a retry republishes this page (see above).
        patchIntent(requestKey, { hostedAppId: published.appId });
        // The event the ads optimize for. Most first publishes happen here; after a failed one
        // the app goes online from the project — Publish, or its first chat edit, which publishes
        // itself — and publishLatest sends it then. A republish of a page an earlier attempt
        // already published is not a new one.
        if (!hosted) trackPixel('AppPublished');

        /*
         * Now the app exists, so the things that hang off it can go up.
         *
         * Both are best-effort and neither un-publishes the app. A document that fails to
         * store leaves an app whose reader has nothing to read, which is worth saying — but
         * it is not worth throwing away a build and an address that both worked.
         */
        if (doc && textAsset) {
          try {
            await uploadTextAsset(published.appId, { ...doc, name: textAsset.name });
            // The MARKER only — never the text. This is what lets a re-publish re-attach it
            // without the document being carried around.
            definition = {
              ...definition,
              textAsset: {
                name: textAsset.name,
                title: doc.title,
                attribution: doc.attribution,
                ...(doc.headingPattern ? { headingPattern: doc.headingPattern } : {}),
              },
            };
          } catch (e) {
            attachmentProblem = e instanceof Error
              ? `The app was built and published, but its text did not store — ${e.message}`
              : 'The app was built and published, but its text did not store.';
          }
        }

        if (failedMedia.length) {
          attachmentProblem = `${failedMedia.length} file${failedMedia.length === 1 ? '' : 's'} did not upload (${failedMedia.join(', ')}). The app is live without ${failedMedia.length === 1 ? 'it' : 'them'}.`;
        }
        /*
         * And any photo the page ASKS for that is not there — said, by name (F4). A build replayed
         * without its files, or written against a photo that was not attached, published a page
         * whose `S.media` answered null, and chat said only "It's live".
         */
        const arrived = new Set(uploadedMedia.map((a) => a.id));
        const absent = mediaNamesIn(built.appCode).filter((n) => !arrived.has(n) && !failedMedia.includes(n));
        if (absent.length) {
          const one = absent.length === 1;
          const sentence = `Your page has ${one ? 'a spot' : 'spots'} for ${one ? 'a photo' : `${absent.length} photos`} it does not have (${absent.join(', ')}), so ${one ? 'it shows' : 'they show'} nothing yet. Attach ${one ? 'it' : 'them'} in chat to fill ${one ? 'it' : 'them'}.`;
          attachmentProblem = attachmentProblem ? `${attachmentProblem}\n\n${sentence}` : sentence;
        }
      } catch (e) {
        publishError = e instanceof Error ? e.message : 'Publishing failed.';
        /*
         * The row it registered goes with the failure. Written down at once, so a retry of this
         * intent reuses it, and onto the project once it is saved, for the publish from Settings.
         */
        registeredAppId = registeredAppIdOf(e) ?? registeredAppId;
        if (registeredAppId) patchIntent(requestKey, { registeredAppId });
      }
      /*
       * Kept on the record, so every later publish from this project sends them again — and
       * whether or not the page went up. They are uploaded BEFORE the host call, so one that
       * fails leaves them in storage and gone from the draft; dropping the list with it meant
       * "publish it from Settings" sent no photos and `S.media` answered null. The addresses
       * read the same under whichever row lists them, so the app registered on that later
       * publish can carry them as they are.
       *
       * The catalogue id that was registered for them is NOT kept: a record with an id reads
       * as an app that has been online (the AppPublished count, the Settings actions), and
       * this one never was.
       */
      if (uploadedMedia.length) definition = { ...definition, assets: uploadedMedia };
      /*
       * What a failed publish says about the photos: which went up and which did not.
       *
       * They go up only once the app is registered, so a register that failed (offline, a 5xx)
       * uploaded none of them — and the draft, the only copy of the files, is reset below. The
       * message said just "publish it from Settings", whose publish sends no photos and checks
       * for none: the page went live with empty spots and nobody said the pictures were gone
       * (review of lane/build-publish, 2026-09-30). So each one that is not in storage is named.
       */
      let photosAfterFailure = '';
      if (publishError && draft.attachedMedia.length) {
        const stored = new Set(uploadedMedia.map((a) => a.id));
        const notUp = draft.attachedMedia.map((m) => m.name).filter((n) => !stored.has(n));
        if (!notUp.length) photosAfterFailure = ' Your photos are saved and go up with it.';
        else {
          const kept = uploadedMedia.length
            ? ` The photo${uploadedMedia.length === 1 ? '' : 's'} that did go up (${listed(uploadedMedia.map((a) => a.id))}) ${uploadedMedia.length === 1 ? 'is' : 'are'} saved and go${uploadedMedia.length === 1 ? 'es' : ''} up with it.`
            : '';
          const them = notUp.length === 1 ? 'it' : 'them';
          photosAfterFailure = `${kept}\n\n${listed(notUp)} did not go up, so the page will have nothing for ${them}. Once it is online, attach ${them} in chat.`;
        }
      }

      // And again: a switch made while it was being published must not have it saved as this.
      await assertStillSignedInAs(ownerId);
      set({ phase: 'saving' });
      const project = await save(reservedId, { name: definition.name, description: goal, definition }, ownerId);
      saved = true;
      if (registeredAppId) keepUnhostedApp(project.id, registeredAppId);
      else forgetUnhostedApp(project.id);

      const now = new Date().toISOString();
      await chatStore().addMessage(project.id, { role: 'user', content: goal, timestamp: now });
      /*
       * The first message of the app's own chat, so it is read INSIDE the project: it points at
       * Settings, where Publish is, never at "the project". Plain text, as on the phone (see
       * src/onboarding/runWebAppBuild.ts): the name went in `**…**`, which a client that shows
       * text as text printed as asterisks. ChatPanel still bolds the ones already stored.
       */
      await chatStore().addMessage(project.id, {
        role: 'assistant',
        content: siteUrl
          // An attachment that did not arrive is said here rather than left to be discovered
          // by opening the app and finding it empty.
          /*
           * With its address, as on the phone: "Share the link" with no link sent the owner
           * looking for it. Plain text — the top bar's "Open your site" is the one to click.
           */
          ? `I built ${definition.name} as a web app you can open in any browser.${servedAt ? ` It's live at ${displayAddress(servedAt)}.` : ''} Share the link with anyone.${attachmentProblem ? `\n\n${attachmentProblem}` : ''}`
          : `I built ${definition.name}, but couldn't publish it just yet — ${publishError} Your app is saved; publish it from Settings to put it online.${photosAfterFailure}`,
        timestamp: new Date().toISOString(),
        appDefinitionSnapshot: JSON.stringify(definition),
      });

      // Landed, so the intent is over and the next build is a new one. Only now: a save or a
      // summary that throws keeps the key, so Try again is answered by the server's replay.
      clearIntent(requestKey);
      attempt = null;
      /*
       * And the draft is over too (F2, 2026-09-30). Kept, the finished app's photos, look and
       * document rode into the next idea: a café built after the wedding sent the wedding's
       * photos, uploaded them to the café's catalogue id and put them on its page.
       *
       * Only while it still IS this build's draft. The build ran from a snapshot and the overlay
       * can be put away, so the draft may by now be the next idea, typed while this one ran —
       * and that is somebody's unsaved work, not this app's leftovers. A new description already
       * dropped this app's files and look when it was typed (`setDescription`), so nothing of
       * this build rides along in it.
       */
      if (useDraft.getState().description.trim() === goal) useDraft.getState().reset();
      if (get().minimised) {
        // Put away: offered, not imposed (see `landed`).
        set({ status: 'idle', phase: 'queued', error: null, projectId: null, minimised: false, landed: { projectId: project.id, name: definition.name } });
        return;
      }
      set({ status: 'idle', phase: 'queued', error: null, projectId: null });
      /*
       * To Chat, where the editor opens: there is no preview view any more, and the top bar's
       * "Open your site" is the way to see what was built. REPLACING the wizard, so the browser's
       * Back cannot land on /new/plan and its Build — which, with the intent cleared, minted a new
       * key and charged for a second copy of the app just built.
       */
      navigate?.(`/project/${project.id}`, { replace: true });
    } catch (e) {
      // Out of credits and a refused prompt are not things retrying will fix, so the
      // overlay must not offer a button that cannot work.
      const terminal =
        e instanceof ApiError && (e.kind === 'out_of_credits' || e.kind === 'refused' || e.kind === 'unauthorized');
      const lostWhileGenerating = e instanceof ApiError
        && (e.kind === 'offline' || e.kind === 'timeout')
        && get().phase === 'generating';
      const error = isBuildInProgress(e)
        ? STILL_RUNNING
        : lostWhileGenerating
          ? PROBABLY_RUNNING
          : e instanceof Error ? e.message : 'That build did not finish.';
      /*
       * The intent is KEPT, marked told — even on a terminal failure. The key is what lets the
       * next attempt of this same idea collect a build the server finished after we stopped
       * listening, and it is safe to reuse after the terminal ones: the server refuses sign-in
       * before it claims a key, and releases the claim when it refuses for credits or content.
       * A different idea does not inherit it — `sameIntent` sees to that.
       */
      patchIntent(requestKey, { reported: true });
      if (reservedId && !saved) {
        void noteUnfinished(reservedId, ownerId, `${error} Your choices are saved — Try again builds it from them.`);
      }
      set({
        status: 'error',
        // A failure always comes back out of the pill — and out from behind the offer.
        minimised: false,
        behindOffer: false,
        error,
        terminal,
        outOfCredits: e instanceof ApiError && e.kind === 'out_of_credits',
        errorKind: e instanceof ApiError ? e.kind : null,
      });
    }
  },

  collect: async (ownerId: string) => {
    const stored = readIntent();
    if (!stored || (stored.ownerId !== ownerId && stored.ownerId !== '') || !resumableIntent(stored)) return false;
    if (get().status === 'running') return true;
    if (!useDraft.getState().description.trim()) restoreDraft(stored);
    await get().start(ownerId, inputFromIntent(stored), { behindOffer: true });
    return true;
  },

  retry: () => {
    if (!lastOwnerId) return;
    /*
     * The build that failed, never the draft as it reads now. "Keep browsing" lets the draft
     * change under a running build — /new, a second idea typed — and Try again used to build
     * THAT: idea B, planless, charged as a new build in a new row, with A's reserved row left
     * unfinished (review of F15, 2026-09-30). The draft is not touched, so B is still there.
     *
     * After a reload there is no attempt in memory: then the build `recover()` reported, or —
     * with an empty draft — the record, which is the one copy of the choices left.
     */
    const stored = readIntent();
    const fromRecord = !attempt && !useDraft.getState().description.trim() && stored?.ownerId === lastOwnerId
      ? stored
      : null;
    const intent = reported ?? fromRecord;
    if (!intent) {
      void get().start(lastOwnerId, attempt ?? undefined);
      return;
    }
    const input = inputFromIntent(intent);
    /*
     * Never without the files it was built with (F4). A stored intent's files did not survive the
     * page, and building anyway replayed code naming photos that were never uploaded. Back to
     * where they are chosen instead, with the choices in the draft the wizard reads — the one
     * place the draft IS written, because choosing the files again happens there. Build from
     * there sends the same key when they are the same files.
     */
    if (missingFiles(intent, input)) {
      useDraft.getState().reset();
      restoreDraft(intent);
      chooseFilesAgain();
      return;
    }
    void get().start(lastOwnerId, input);
  },

  dismiss: () => {
    /*
     * Closing the overlay does not un-ask for the app. The record is marked told and KEPT: the
     * reserved row still offers Try again, and the key is what lets that collect a build the
     * server finished after we stopped waiting, instead of paying for it twice. Dropping it
     * here is what made a timed-out build unrecoverable.
     */
    const intent = readIntent();
    if (intent && !intent.reported) writeIntent({ ...intent, reported: true });
    reported = null;
    attempt = null;
    set({ status: 'idle', phase: 'queued', error: null, terminal: false, outOfCredits: false, errorKind: null, minimised: false });
  },

  minimise: () => {
    if (get().status === 'running') set({ minimised: true });
  },

  expand: () => set({ minimised: false }),

  openLanded: () => {
    const landed = get().landed;
    if (!landed) return;
    set({ landed: null });
    navigate?.(`/project/${landed.projectId}`);
  },

  forgetLanded: () => set({ landed: null }),

  getCredits: () => {
    // Closed exactly as Close closes it — the intent and its reserved row are kept, so once
    // credits are bought the row's Try again builds this same idea under the same key.
    get().dismiss();
    navigate?.('/credits');
  },

  recover: (ownerId: string) => {
    if (!ownerId || get().status !== 'idle') return;
    /*
     * A build this browser started and never heard the end of: the tab was discarded or
     * reloaded while it ran. REPORTED, never restarted — spending a build the person did not
     * just ask for is worse than saying what happened — and reported once, by marking the
     * record rather than deleting it, because the key and the row are what Try again needs.
     */
    const intent = readIntent();
    if (!intent || intent.reported || intent.ownerId !== ownerId || !resumableIntent(intent)) return;
    if (mayStillBeRunning(intent)) return;
    writeIntent({ ...intent, reported: true });
    lastOwnerId = ownerId;
    reported = intent;
    set({
      status: 'error',
      phase: 'queued',
      terminal: false,
      outOfCredits: false,
      errorKind: null,
      projectId: intent.reservedId ?? null,
      error:
        `“${intent.appName}” stopped before it finished — the page closed while it was building.`
        + (intent.hadAttachment ? filesLeftWith(intent) : '\n\nYour idea is saved, so Try again picks it up. If it finished on our side meanwhile, it comes back without a second charge.'),
    });
  },

  resumeProject: async (project: Project, ownerId: string) => {
    if (get().status === 'running') return;
    const def = project.definition as AppDefinition;
    const goal = (project.description || def.description || '').trim();
    const stored = readIntent();
    const same = stored && stored.ownerId === ownerId
      && (stored.reservedId === project.id || sameIntent(stored, { appName: project.name, goal }))
      ? stored
      : null;
    /*
     * The choices go back where the build reads them — the idea, the name, the plan (its
     * features, screens, layout and look), the kit — from the row and the record, onto a
     * clean draft so nothing half-made for another app rides along. A document or picture
     * still in memory for this same idea stays; after a reload there is none to keep.
     */
    const draft = useDraft.getState();
    const keep = draft.description.trim() === goal
      ? { attachedDoc: draft.attachedDoc, attachedMedia: draft.attachedMedia }
      : {};
    draft.reset();
    restoreDraft({
      goal,
      appName: project.name,
      plan: def.plan ?? same?.plan ?? null,
      vibe: same?.vibe ?? null,
      styleBrief: same?.styleBrief ?? null,
      kind: def.kind ?? same?.kind,
    });
    useDraft.setState(keep);
    // The row's Try again is a resume too, and must not build without the files either (F4).
    if (missingFiles(same, useDraft.getState())) {
      lastOwnerId = ownerId;
      chooseFilesAgain();
      return;
    }
    // This row IS the intent's reservation, so the app lands here rather than beside it. The
    // key rides along only while the server can still replay under it (`resumableIntent`).
    const resumable = resumableIntent(same);
    const hostedAppId = def.hostedAppId ?? same?.hostedAppId;
    writeIntent({
      ownerId,
      appName: project.name,
      goal,
      signedIn: true,
      hadAttachment: false,
      ...(resumable?.requestKey ? { requestKey: resumable.requestKey } : {}),
      ...(resumable?.startedAt ? { startedAt: resumable.startedAt } : {}),
      reservedId: project.id,
      ...(hostedAppId ? { hostedAppId } : {}),
      reported: true,
    });
    await get().start(ownerId);
  },
}));
